/**
 * 鳥取県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 11品が農水省「うちの郷土料理」鳥取県の27品から、1品（松葉がに）が鳥取県公式から。
 *
 * 形のばらつきを最優先で組んである。
 *  平たい一枚（板わかめ）／棒状2本（とうふちくわ・あごちくわ）／細長い個体（もさえび）／
 *  平たい包みが重なる（柿の葉寿司）／三角（いただき）／脚が横に広がる（松葉がに）。
 *  丸い器盛りはゼロ。これまでの県で丸い形に寄りすぎた反省を反映している。
 *
 * かにを2品置いている理由。
 *  Lv11 親ガニ（ズワイガニのメス）は他県で未使用。Lv12 松葉がに（オス）は兵庫と同名になる
 *  （2026-10-05 兵庫は「津居山かに」に改名したので、同名ではなくなった）。
 *  鳥取県はズワイガニを「松葉がに（オス）」「親がに（メス）」「若松葉がに」の3区分で扱うので、
 *  その区分と県内の水揚げ港（田後・網代・賀露・境港）を差別化の根拠にしてある。
 *  小さいメス → 大きいオス という並びは、マージゲームの視覚的な流れとも合う。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 鳥取砂丘の「日本最大の砂丘」は国・県の断定形が確認できない。使わない。
 *  - 「かに水揚げ量日本一」も年で変わる。使わない。トン数・順位を書かない。
 *  - 「あごはあごが落ちるほど」の語源は農水省でも伝聞形。書かない。
 *  - らっきょうの参勤交代起源、とうふちくわの藩主の触れ、げたんは同様どれも諸説。断定しない。
 *  - 水木しげる関連は著作権のため一切扱わない。境港は水産・港としてのみ。
 *
 * 本番画像は public/assets/foods/tottori/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：おいり・あごちくわ・栃餅・柿の葉寿司・いただき・親ガニ（店で食べられる品に差し替え）
 * - ホルモン焼きそば：提供店の数（市内約30店）はすぐ変わるので書かない
 * - ホルモン焼きそば：テレビ番組に登場したことは書かない
 * - ホルモン焼きそば：発祥の店は書かない
 * - 鳥取牛骨ラーメン：「60年前に生まれた」は書かれた年が分からず起点が確かめられないので書かない
 * - 鳥取牛骨ラーメン：発祥の店・大連の話は公式資料で確かめられないので書かない
 * - 鳥取牛骨ラーメン：全国放送の番組で2位になったことは書かない
 * - 大山そば：「日本三大牛馬市」は牛馬市の称号で、そばの話ではないので書かない
 * - 大山そば：ご当地認定の3条件のうち「由来を客に説明できること」は図鑑に不要なので書かない
 * - 白いか丼：「イカの大トロ」は宣伝の言い回しなので書かない
 * - 白いか丼：「元祖いか丼」は特定の店が名乗っているものなので書かない。店名も書かない
 * - 鳥取和牛：第1回全国和牛能力共進会の開催年は出典に書かれていないので書かない
 * - 鳥取和牛：「牛肉の中の牛肉」は宣伝の言い回しなので書かない
 * - 鳥取和牛：焼肉店の名は書かない
 * - 紅ずわいがに：「水揚げ日本一」は県観光連盟のかに特集ではかに全体の話として出てくるだけで、紅ずわいがに単独では確かめていないので書かない
 * - 紅ずわいがに：水深・水揚げ量はページに書かれていないので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { tottoriSceneryTour } from './tottoriScenes';
import { TOTTORI_FOOD_HULLS } from './tottoriFoodShapes.generated';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'rakkyo',
    level: 1,
    name: 'らっきょう漬け',
    shortName: 'らっきょう',
    image: '/assets/foods/tottori/01_rakkyo.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '砂の畑でふくらんだ、白い粒。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/rakkyou_zuke_tottori.html',
    icon: { label: 'らっきょう', shape: 'berry', baseColor: '#f0ece0', accentColor: '#c8402a' },
    reward: {
      unlockScore: 200,
      area: '鳥取市福部町・北栄町',
      headline: '砂丘地で育ったらっきょうを塩漬けにしてから甘酢に漬け直す、鳥取の食卓の定番。',
      tip: '収穫は5月下旬から6月中旬。塩漬けを10日から2週間おき、塩抜きしてから甘酢に漬け直す。',
      links: searchLinks('鳥取市福部町', 'らっきょう'),
    },
  },
  {
    id: 'ita-wakame',
    level: 2,
    name: '板わかめ',
    shortName: '板わかめ',
    image: '/assets/foods/tottori/02_ita_wakame.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '日本海を、一枚に干した。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ita_wakame_tottori.html',
    icon: { label: '板わかめ', shape: 'fish', baseColor: '#1e3a2a', accentColor: '#4a7a5a' },
    reward: {
      unlockScore: 500,
      area: '鳥取県沿岸部',
      headline: '生わかめを板に広げて乾かすだけの、味付けを一切しない海の保存食。',
      tip: '収穫から4時間以内に洗い、葉を広げながら板に並べ、およそ24時間かけて低温で乾かす。軽くあぶってごはんにかける。',
      links: searchLinks('鳥取県', '板わかめ'),
    },
  },
  {
    id: 'tofu-chikuwa',
    level: 3,
    name: 'とうふちくわ',
    shortName: 'とうふちくわ',
    image: '/assets/foods/tottori/03_tofu_chikuwa.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '魚ではなく豆腐でつくる、白いちくわ。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/toufu_chikuwa_tottori.html',
    icon: { label: 'とうふちくわ', shape: 'dumpling', baseColor: '#f4f0e4', accentColor: '#d8c090' },
    reward: {
      unlockScore: 1000,
      area: '倉吉市・鳥取市',
      headline: '木綿豆腐と白身魚のすり身を混ぜて蒸し上げた、焼き色のつかない白いちくわ。',
      tip: '豆腐と魚のすり身をおよそ7対3で混ぜて蒸す。焼かないので白いまま。しょうが醤油で食べる。',
      links: searchLinks('鳥取県', 'とうふちくわ'),
    },
  },
  {
    id: 'tottori-horumon-yakisoba',
    level: 4,
    name: 'ホルモン焼きそば',
    shortName: 'ホルそば',
    image: '/assets/foods/tottori/04_tottori_horumon_yakisoba.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '牛のパイプ（小腸）だけを使ったホルモンを中華麺と炒め、店ごとのたれで濃い味に仕上げる焼きそば。鳥取市では「ホルそば」とも呼ばれ、市内の多くの店で出されている。',
    source:
      'https://www.torican.jp/machinaka-men',
    icon: { label: 'ホルそば', shape: 'noodle-bowl', baseColor: '#6a3a1e', accentColor: '#c9a24a' },
    reward: {
      unlockScore: 1800,
      area: '鳥取市',
      headline: '牛のパイプだけを麺と炒める',
      tip: 'ホルモンと野菜を炒めて出していたところへ、焼きそばのように中華麺を入れておなかいっぱいにしたい、という客の求めから生まれたといわれる。',
      links: searchLinks('鳥取市', 'ホルモン焼きそば'),
    },
  },
  {
    id: 'tottori-gyukotsu-ramen',
    level: 5,
    name: '鳥取牛骨ラーメン',
    shortName: '牛骨ラーメン',
    image: '/assets/foods/tottori/05_tottori_gyukotsu_ramen.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '牛の骨からとったスープのラーメン。豚骨・鶏がらに続く3つ目の動物系スープとされ、香ばしい牛の香りと、テールスープを思わせるすっきりした後味がある。鳥取県中部に店が点在している。',
    source:
      'https://gyukotsu.net/',
    icon: { label: '牛骨ラーメン', shape: 'ramen', baseColor: '#d9a64a', accentColor: '#4a8a3a' },
    reward: {
      unlockScore: 3000,
      area: '倉吉市ほか鳥取県中部',
      headline: '牛の骨でとるすっきりしたスープ',
      tip: '2009年6月、県中部の有志が「鳥取牛骨ラーメン応麺団」を立ち上げ、食べ歩きの地図を作って広く知らせてきた。',
      links: searchLinks('鳥取県中部', '牛骨ラーメン'),
    },
  },
  {
    id: 'daisen-soba',
    level: 6,
    name: '大山そば',
    shortName: '大山そば',
    image: '/assets/foods/tottori/06_daisen_soba.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: 'そばの甘皮までたっぷり挽き込んだそば粉で打つため、黒い色と素朴な風味がある。大山観光局のご当地認定店では、大山山麓のそば粉を使い、地元で打つ。長芋とろろや山菜など、上にのせるものは店ごとに違う。',
    source:
      'https://tourismdaisen.com/spot/83/',
    icon: { label: '大山そば', shape: 'noodle-bowl', baseColor: '#5a4a3a', accentColor: '#2a2a2a' },
    reward: {
      unlockScore: 4500,
      area: '大山町',
      headline: '甘皮まで挽き込んだ黒いそば',
      tip: '約800年前、大山寺の基好上人が牛馬を守る札を授け、山麓での牛馬の放牧やそば作りを勧めたのが始まりとされる。',
      links: searchLinks('大山町', '大山そば'),
      official: [{ label: '大山観光局「グルメ・お土産」', url: 'https://tourismdaisen.com/gourmet/' }],
    },
  },
  {
    id: 'daisen-okowa',
    level: 7,
    name: '大山おこわ',
    shortName: '大山おこわ',
    image: '/assets/foods/tottori/07_daisen_okowa.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '大山詣りの弁当だった、蒸したごはん。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/daisen_okowa_tottori.html',
    icon: { label: '大山おこわ', shape: 'roe', baseColor: '#c8a870', accentColor: '#7a5a34' },
    reward: {
      unlockScore: 6500,
      area: '米子市・大山周辺',
      headline: 'もち米に鶏肉と山の幸を合わせて蒸し上げる、大山のふもとの祝いのごはん。',
      tip: '明治期には大山寺の牛馬市で馬喰たちの食事になり、大山詣りの弁当としても親しまれてきた。',
      links: searchLinks('米子市', '大山おこわ'),
    },
  },
  {
    id: 'mosaebi',
    level: 8,
    name: 'もさえびの刺身',
    shortName: 'もさえび',
    image: '/assets/foods/tottori/08_mosaebi.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '深いところから、生のまま。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/mosaebi_no_sashimi_tottori.html',
    icon: { label: 'もさえび', shape: 'fish', baseColor: '#e8b8b0', accentColor: '#8a4a48' },
    reward: {
      unlockScore: 9000,
      area: '境漁港・鳥取港・網代漁港',
      headline: '正式名はクロザコエビ。日本海の深海で獲れるえびを、殻をむいてそのまま刺身にする。',
      tip: '水深200メートルから250メートルにすむ。9月から5月まで沖合底びき網で獲れる。鮮度が落ちやすく、長く地元だけの味だった。',
      links: searchLinks('鳥取県', 'もさえび'),
    },
  },
  {
    id: 'shiroika-don',
    level: 9,
    name: '白いか丼',
    shortName: '白いか丼',
    image: '/assets/foods/tottori/09_shiroika_don.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '夏が旬の白いか（ケンサキイカ）の刺身をご飯にのせる丼。白いかはやわらかく、とろけるような濃い味わいがある。刺身のほか、煮物・焼き物・天ぷら・一夜干し・塩辛にもされる。',
    source:
      'https://www.torican.jp/feature/gourmet_seafood',
    icon: { label: '白いか丼', shape: 'rice-bowl', baseColor: '#f2f0ea', accentColor: '#6a9a4a' },
    reward: {
      unlockScore: 12000,
      area: '鳥取市（賀露港）',
      headline: '夏の白いかを刺身でのせる',
      tip: '白いかは、日本海に浮かぶイカ釣り漁船の漁火に集まったところを一本釣りで釣り上げる。漁火は鳥取の夏の風物詩とされる。',
      links: searchLinks('鳥取市', '白いか丼'),
    },
  },
  {
    id: 'tottori-wagyu',
    level: 10,
    name: '鳥取和牛',
    shortName: '鳥取和牛',
    image: '/assets/foods/tottori/10_tottori_wagyu.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '鳥取で育つ和牛。全国和牛能力共進会の第1回大会では、鳥取の「気高号」が肉牛部門で1位になった。赤身のこくと脂のうまみが合わさった肉で、焼肉では部位ごとに味の違いを楽しむ。',
    source:
      'https://www.torican.jp/machinaka-niku',
    icon: { label: '鳥取和牛', shape: 'feast', baseColor: '#c0485a', accentColor: '#f2e6e0' },
    reward: {
      unlockScore: 16000,
      area: '鳥取市ほか県内',
      headline: '第1回の共進会で1位の血統',
      tip: '脂のうまみ成分とされるオレイン酸を55%以上含む鳥取和牛は「オレイン55」の名で売られている。',
      links: searchLinks('鳥取市', '鳥取和牛 焼肉'),
    },
  },
  {
    id: 'beni-zuwaigani',
    level: 11,
    name: '紅ずわいがに',
    shortName: '紅ずわいがに',
    image: '/assets/foods/tottori/11_beni_zuwaigani.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'ゆでる前から鮮やかな紅色をしたかに。みずみずしくやわらかな身と豊かな甘みがある。9月の漁の解禁とともに鳥取のかにの季節の始まりを告げ、旬の目安は9月から6月と、松葉がにより長く味わえる。',
    source:
      'https://kanitoriken.jp/crab/',
    icon: { label: '紅ずわい', shape: 'sashimi-platter', baseColor: '#c0281e', accentColor: '#f2a46a' },
    reward: {
      unlockScore: 22000,
      area: '境港市',
      headline: 'ゆでる前から紅いかに',
      tip: '鳥取県では主に境漁港に水揚げされる。かにすき、ゆでがに、かに丼、寿司、かに汁など、さまざまな料理にされる。',
      links: searchLinks('境港市', '紅ずわいがに'),
      official: [{ label: '鳥取県「蟹取県 ウェルカニキャンペーン」', url: 'https://kanitoriken.jp/' }],
    },
  },
  {
    id: 'matsubagani',
    level: 12,
    name: '松葉がに',
    shortName: '松葉がに',
    image: '/assets/foods/tottori/12_matsubagani.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '冬の日本海から、長い脚ごと。',
    source: 'https://www.pref.tottori.lg.jp/92139.htm',
    icon: { label: '松葉がに', shape: 'feast', baseColor: '#c4442a', accentColor: '#f0d8b8' },
    reward: {
      unlockScore: 30000,
      area: '田後港・網代港・鳥取港（賀露）・境港',
      headline: 'ズワイガニのオスを鳥取では松葉がにと呼ぶ。冬の日本海から、長い脚ごと水揚げされる。',
      tip: '鳥取県はズワイガニの水揚げ状況を公表している。県内の水揚げは田後、網代、賀露（鳥取港）、境港の各港にまたがる。',
      links: searchLinks('鳥取県', '松葉がに'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const tottoriFoodStage: StageDefinition = {
  regionId: 'tottori',
  regionName: '鳥取県',
  mode: 'food',
  title: '鳥取めしマージ',
  theme: {
    primaryColor: '#2a5a7a',
    secondaryColor: '#d8b878',
    accentColor: '#e8cc90',
    backgroundColor: '#121820',
  },
  /** 背景ツアー「鳥取めぐり」。軸は一日の時間。夜明け前の港から、夜の三朝温泉へ。 */
  scenery: {
    tour: tottoriSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, TOTTORI_FOOD_HULLS),
};
